import { apiClient } from './apiClient';
import { getUserProfile } from './auth';

export const EVENT_SEVERITIES = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  DEBUG: 'debug',
} as const;

export const EVENT_CATEGORIES = {
  RLS_REJECTION: 'rls_rejection',
  API_ERROR: 'api_error',
  NETWORK_ERROR: 'network_error',
  AUTH_ERROR: 'auth_error',
  VALIDATION_ERROR: 'validation_error',
  EDGE_FUNCTION_ERROR: 'edge_function_error',
  UNEXPECTED_STATE: 'unexpected_state',
  DATA_LOAD: 'data_load',
  USER_ACTION: 'user_action',
  WORKFLOW: 'workflow',
  NAVIGATION: 'navigation',
  SAVE_OPERATION: 'save_operation',
  PHOTO_UPLOAD: 'photo_upload',
} as const;

export type EventSeverity = typeof EVENT_SEVERITIES[keyof typeof EVENT_SEVERITIES];
export type EventCategory = typeof EVENT_CATEGORIES[keyof typeof EVENT_CATEGORIES];

interface EventLogParams {
  severity: EventSeverity;
  category: EventCategory;
  message: string;
  metadata?: Record<string, unknown> | null;
}

export async function logEvent(params: EventLogParams): Promise<void> {
  try {
    const profile = await getUserProfile();
    const enrichedMetadata: Record<string, unknown> = {
      ...(params.metadata || {}),
      user_role: profile?.role ?? '',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      timestamp_local: new Date().toISOString(),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };

    await apiClient.post('/api/event-logs', {
      severity: params.severity,
      category: params.category,
      message: params.message,
      metadata: enrichedMetadata,
    });
  } catch (err) {
    console.error('[EventLog] Failed to write event log:', err, params);
  }
}

export interface ScopedLogger {
  debug: (message: string, metadata?: Record<string, unknown>, category?: EventCategory) => void;
  info: (message: string, metadata?: Record<string, unknown>, category?: EventCategory) => void;
  warn: (message: string, metadata?: Record<string, unknown>, category?: EventCategory) => void;
  error: (message: string, metadata?: Record<string, unknown>, category?: EventCategory) => void;
}

export function createLogger(componentName: string): ScopedLogger {
  const log = (severity: EventSeverity, message: string, metadata?: Record<string, unknown>, category?: EventCategory) => {
    logEvent({
      severity,
      category: category || EVENT_CATEGORIES.WORKFLOW,
      message: `[${componentName}] ${message}`,
      metadata: { ...metadata, componentName },
    });
  };

  return {
    debug: (message, metadata, category) => log(EVENT_SEVERITIES.DEBUG, message, metadata, category),
    info: (message, metadata, category) => log(EVENT_SEVERITIES.INFO, message, metadata, category),
    warn: (message, metadata, category) => log(EVENT_SEVERITIES.WARNING, message, metadata, category),
    error: (message, metadata, category) => log(EVENT_SEVERITIES.ERROR, message, metadata, category),
  };
}
